import { defaultBrandNavigationPaginationConfig } from './../../../types/api/brand';
import { getters } from './getters';
import { defineStore, type StateTree } from 'pinia';
import { usePaginationCDLMap, useFileConverter, useTreeData } from '@centric-os/helpers';
import {
  type CDLQueryParams,
  type RecordEntityId,
  type RecordList,
  type Brand,
  type BrandStatus,
  type ApiResponseError,
  SortOrder,
  type BrandDiffRecord,
  type BrandDiffPayload,
  BrandStatusType,
  BrandStatusSeverity,
  type TreeNode,
  type BrandPublishError,
  type StationBrand,
  type Menu,
} from '@centric-os/types';
import { isAxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { debounce, isNil, cloneDeep, isEmpty, isEqual, merge, type DebouncedFunc } from 'lodash-es';

const refreshDebounceMap = new WeakMap<
  object,
  DebouncedFunc<(id: RecordEntityId, params?: CDLQueryParams) => Promise<void>>
>();

const statusesDebounceMap = new WeakMap<
  object,
  DebouncedFunc<(id: RecordEntityId) => Promise<void>>
>();

// Carry a separately-fetched parent (fetchBrandParent) onto a freshly-loaded brand:
// keep it for the same brand, drop it for a different one so nothing stale shows.
function preserveParent(
  next: Pick<Brand, 'id' | 'parent'>,
  current: Brand | null,
): Brand['parent'] {
  return next.parent ?? (next.id === current?.id ? current?.parent : undefined);
}

export interface State extends StateTree {
  brand: Brand;
  brands: Brand[];
  isPublished: boolean;
  brand_updates: TreeNode[];
  selectedDiff: TreeNode;
  brandLocalOverwriteKeys: string[];
  brandPublishErrors: BrandPublishError[];
  brandStations: StationBrand[];
}
export function useBrandStore(storeId = 'brand') {
  const store = defineStore(storeId, {
    state: (): State => ({
      brand: null,
      brands: [],
      isPublished: false,
      brand_updates: [],
      selectedDiff: null,
      brandLocalOverwriteKeys: [],
      brandPublishErrors: [],
      brandStations: [],
    }),
    actions: {
      async fetchBrandsEndpoint(
        globalMenuGroupId: RecordEntityId,
        noPagination = false,
        config: AxiosRequestConfig = {},
      ) {
        const { mapCentricOSPaginationMeta, mapMenuQueryParams } = usePaginationCDLMap();

        const defaultParams: CDLQueryParams = {
          sort_by: 'name',
          sort_order: SortOrder.ASC,
          relationships: ['statuses'],
        };

        let params = noPagination
          ? defaultParams
          : {
              ...mapMenuQueryParams(this.pagination.queryParams),
              relationships: defaultParams.relationships,
            };

        if (!isEmpty(config?.params)) {
          params = merge({}, params, config.params);
        }

        const response = await this.cdlApi.get<RecordList<Brand>>(
          `/menu/v3/draft/global-menu-group/${globalMenuGroupId}/brands`,
          {
            ...config,
            signal: this.abortControllers.get('fetchBrandsEndpoint')?.signal,
            params,
          },
        );
        const { results, meta } = response.data;

        this.brands = results as Brand[];

        if (!noPagination) {
          this.updatePaginationWithMeta(mapCentricOSPaginationMeta(meta));
        }
      },
      async getMenuGroupBrands(
        globalMenuGroupId: RecordEntityId,
        noPagination = false,
        config: AxiosRequestConfig = {},
      ) {
        await this.fetchBrandsEndpoint(globalMenuGroupId, noPagination, config);
      },
      async getBrands(params?: CDLQueryParams) {
        const response = await this.cdlApi.get<RecordList<Brand>>(`/menu/v3/draft/brands`, {
          params,
        });
        const { results } = response.data;
        this.brands = results;
      },
      async fetchBrandEndpoint(id: RecordEntityId, params?: CDLQueryParams): Promise<void> {
        if (isEmpty(params)) {
          params = {
            relationships: ['statuses'],
          };
        }
        const response = await this.cdlApi.get<Brand>(`/menu/v3/draft/brand/${id}`, {
          signal: this.abortControllers.get('fetchBrandEndpoint')?.signal,
          params,
        });
        this.brand = response.data;
      },
      async fetchV4BrandEndpoint(id: RecordEntityId, params?: CDLQueryParams): Promise<void> {
        const response = await this.cdlApi.get<Brand>(`/menu/v4/brand/${id}`, {
          params,
        });
        this.brand = { ...response.data, parent: preserveParent(response.data, this.brand) };
      },

      async getV3Brand(id: RecordEntityId, params?: CDLQueryParams): Promise<Brand> {
        if (!id) return;

        const response = await this.cdlApi.get<Brand>(`/menu/v3/draft/brand/${id}`, {
          signal: this.abortControllers.get('getV3Brand')?.signal,
          params,
        });
        this.brand = response.data;
        return response.data;
      },
      async getV4Brand(id: RecordEntityId, params?: CDLQueryParams): Promise<Brand> {
        const response = await this.cdlApi.get<Brand>(`/menu/v4/brand/${id}`, {
          signal: this.abortControllers.get('getV4Brand')?.signal,
          params,
        });
        const { menus, ...restOfBrand } = response.data;
        this.brand = { ...restOfBrand, parent: preserveParent(restOfBrand, this.brand) };
        return response.data;
      },
      async fetchBrandStatuses(id: RecordEntityId): Promise<void> {
        if (!id) return;
        let exec = statusesDebounceMap.get(this);
        if (!exec) {
          // Debounce mirrors getBrandForRefresh: callers (long-polling and ad-hoc
          // refresh paths) can fire in quick succession, and tests register new MSW
          // handlers in the same tick they expect polling to pick up. The 500ms
          // window collapses bursts to a single fetch and lets newly-registered
          // handlers win.
          exec = debounce(async (statusesId: RecordEntityId) => {
            if (this.brand?.id !== statusesId) return;
            const response = await this.cdlApi.get<{ results: BrandStatus[] }>(
              `/menu/v4/brand/${statusesId}/statuses`,
            );
            // Race guard: brand may have changed while the request was in flight.
            if (
              this.brand?.id === statusesId &&
              !isEqual(this.brand.statuses, response.data.results)
            ) {
              // Write in place: replacing `brand` re-runs every computed that reads it, even when only statuses changed.
              this.brand.statuses = response.data.results;
            }
          }, 500);
          statusesDebounceMap.set(this, exec);
        }
        exec(id);
      },
      async fetchBrandParent(id: RecordEntityId): Promise<Brand['parent']> {
        if (!id) return undefined;
        try {
          const response = await this.cdlApi.get<Brand>(`/menu/v4/brand/${id}/parent`, {
            signal: this.abortControllers.get('fetchBrandParent')?.signal,
          });
          return response.data;
        } catch (err) {
          // A 404 means the brand genuinely has no parent -> clear it. Any other error
          // (5xx, network, or the endpoint not yet deployed) is transient: keep whatever
          // parent is already loaded rather than wiping the banner and sync buttons.
          if (isAxiosError(err) && err.response?.status === 404) return undefined;
          return this.brand?.id === id ? this.brand?.parent : undefined;
        }
      },
      setBrandParent(id: RecordEntityId, parent: Brand['parent']): void {
        // Race guard: only apply if the store still holds the brand we fetched for.
        if (this.brand?.id === id) {
          this.brand = { ...this.brand, parent };
        }
      },
      async getBrandForRefresh(id: RecordEntityId, params?: CDLQueryParams): Promise<void> {
        let exec = refreshDebounceMap.get(this);
        if (!exec) {
          exec = debounce(async (refreshId: RecordEntityId, refreshParams?: CDLQueryParams) => {
            // As this is fired on a debounce, we need to check if the brand has changed
            if (this.brand?.id !== refreshId) {
              return;
            }
            if (this.brand.catalog_id) {
              await this.fetchV4BrandEndpoint(refreshId, refreshParams);
            } else {
              await this.fetchBrandEndpoint(refreshId, refreshParams);
            }
          }, 500);
          refreshDebounceMap.set(this, exec);
        }
        exec(id, params);
      },
      cancelBrandRefresh(): void {
        refreshDebounceMap.get(this)?.cancel();
      },
      cancelBrandStatuses(): void {
        statusesDebounceMap.get(this)?.cancel();
      },

      async createV4Brand(payload: Partial<Brand>) {
        const { getBase64 } = useFileConverter();

        const newPayload = JSON.parse(JSON.stringify(payload));
        delete newPayload.attachments.thumbnail;

        let newBrandId: RecordEntityId = null;
        await this.cdlApi
          .post<Brand, AxiosResponse<Brand, any>, Partial<Brand>>('/menu/v4/brand', newPayload)
          .then(async (response) => {
            if (!isNil(payload.attachments.thumbnail)) {
              const imageBase64 = await getBase64(payload.attachments.thumbnail);

              newBrandId = response.data.id;

              await this.cdlApi
                .post<any, AxiosResponse<any, any>, Partial<any>>(
                  `/menu/v4/brand/${newBrandId}/attachment/thumbnail`,
                  {
                    data: imageBase64,
                  },
                )
                .catch(async () => {
                  await this.deleteV4Brand(newBrandId);
                  const error: ApiResponseError = {
                    status: 400,
                    message: 'Image upload error',
                    path: null,
                    method: null,
                    timestamp: new Date(),
                  };
                  return Promise.reject(error);
                });
            }
          });
      },
      // THIS FLOW IS DIFFERENT THAN USUAL BECAUSE WE ARE DOING A MANUAL ROLLBACK
      async createBrand(payload: Partial<Brand>) {
        const { getBase64 } = useFileConverter();

        const newPayload = JSON.parse(JSON.stringify(payload));
        delete newPayload.attachments.thumbnail;

        let newBrandId: RecordEntityId = null;
        await this.cdlApi
          .post<
            Brand,
            AxiosResponse<Brand, any>,
            Partial<Brand>
          >('/menu/v3/draft/brand', newPayload)
          .then(async (response) => {
            if (!isNil(payload.attachments.thumbnail)) {
              const imageBase64 = await getBase64(payload.attachments.thumbnail);

              newBrandId = response.data.id;

              await this.cdlApi
                .post<any, AxiosResponse<any, any>, Partial<any>>(
                  `/menu/v3/draft/brand/${newBrandId}/attachment/thumbnail`,
                  {
                    data: imageBase64,
                  },
                )
                .catch(async () => {
                  await this.deleteBrand(newBrandId);
                  const error: ApiResponseError = {
                    status: 400,
                    message: 'Image upload error',
                    path: null,
                    method: null,
                    timestamp: new Date(),
                  };
                  return Promise.reject(error);
                });
            }
          });
      },
      async updateBrand(payload: Partial<Brand>) {
        if (payload && payload.id) {
          const { version, statuses, attachments, ...restOfPayload } = payload;

          const newPayload: Partial<Brand> = {
            ...restOfPayload,
            attachments: attachments?.thumbnail instanceof File ? {} : attachments,
          };

          await this.cdlApi
            .patch<Brand, AxiosResponse<Brand, any>, Partial<Brand>>(
              `/menu/v3/draft/brand/${newPayload.id}`,
              {
                ...newPayload,
              },
            )
            .then(async () => {
              if (payload.attachments?.thumbnail instanceof File) {
                const { getBase64 } = useFileConverter();

                const imageBase64 = await getBase64(payload.attachments.thumbnail);

                await this.cdlApi.post<any, AxiosResponse<any, any>, Partial<any>>(
                  `/menu/v3/draft/brand/${payload.id}/attachment/thumbnail`,
                  {
                    data: imageBase64,
                  },
                );
              }
            });
        } else {
          return Promise.reject(new Error('no brand selected'));
        }
      },
      async updateV4Brand(payload: Partial<Brand>) {
        if (payload && payload.id) {
          const { version, statuses, attachments, ...restOfPayload } = payload;

          const newPayload: Partial<Brand> = {
            ...restOfPayload,
            attachments: attachments?.thumbnail instanceof File ? {} : attachments,
          };

          await this.cdlApi
            .patch<Brand, AxiosResponse<Brand, any>, Partial<Brand>>(
              `/menu/v4/brand/${newPayload.id}`,
              {
                ...newPayload,
              },
            )
            .then(async () => {
              if (payload.attachments?.thumbnail instanceof File) {
                const { getBase64 } = useFileConverter();

                const imageBase64 = await getBase64(payload.attachments.thumbnail);

                await this.cdlApi.post<any, AxiosResponse<any, any>, Partial<any>>(
                  `/menu/v4/brand/${payload.id}/attachment/thumbnail`,
                  {
                    data: imageBase64,
                  },
                );
              }
            });
        } else {
          return Promise.reject(new Error('no brand selected'));
        }
      },
      async deleteBrand(id: RecordEntityId) {
        await this.cdlApi.delete<Brand, AxiosResponse<Brand, any>, Partial<Brand>>(
          `/menu/v3/draft/brand/${id}`,
        );
      },
      async deleteV4Brand(id: RecordEntityId): Promise<void> {
        await this.cdlApi.delete<Brand, AxiosResponse<Brand, any>, Partial<Brand>>(
          `/menu/v4/brand/${id}`,
        );
      },
      async publishBrand(brandId: RecordEntityId) {
        if (!brandId) {
          throw new Error('Brand ID is required');
        }

        await this.cdlApi.post<any, AxiosResponse<any, any>, Partial<any>>(
          `/menu/v3/draft/brand/${brandId}/publish`,
        );
      },
      async publishV4Brand(brandId: RecordEntityId): Promise<string | undefined> {
        const response = await this.cdlApi.post<
          { status: string; process_id: string },
          AxiosResponse<{ status: string; process_id: string }>
        >(`/menu/v4/brand/${brandId}/publish`);
        return response.data?.process_id;
      },
      async syncBrandIntegrations(brandId: RecordEntityId) {
        await this.cdlApi.post<{ status: string }>(`/menu/v4/brand/${brandId}/integration-sync`);
      },
      async archiveBrand(id: RecordEntityId): Promise<Brand> {
        const response = await this.cdlApi.post<Brand, AxiosResponse<Brand>>(
          `/menu/v3/draft/brand/${id}/archive`,
        );
        return response.data;
      },
      async unarchiveBrand(id: RecordEntityId): Promise<Brand> {
        const response = await this.cdlApi.post<Brand, AxiosResponse<Brand>>(
          `/menu/v3/draft/brand/${id}/unarchive`,
        );
        return response.data;
      },
      async getBrandUpdates(id: RecordEntityId) {
        const { toTreeData } = useTreeData();

        const params: CDLQueryParams = {
          sort_by: 'name',
          sort_order: SortOrder.ASC,
        };

        const response = await this.cdlApi.get<RecordList<BrandDiffRecord>>(
          `/menu/v3/draft/brand/${id}/diffs`,
          { params: params },
        );
        const { results } = response.data;
        // TEMPORARY WHILE PARAMS ARE IMPL ON BE SIDE
        // Sort the array by the 'name' property in ascending order
        results?.toSorted((a, b) => {
          const nameA = a.name.toUpperCase(); // Convert names to uppercase for case-insensitive sorting
          const nameB = b.name.toUpperCase();

          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0; // Names are equal
        });

        this.brand_updates = toTreeData(results);
      },
      async generateDiff(brandId: RecordEntityId) {
        await this.cdlApi.post<any, AxiosResponse<any, any>, Partial<any>>(
          `/menu/v3/draft/brand/${brandId}/diff/generate`,
        );
      },
      async applyChanges(brandId: RecordEntityId, payload: BrandDiffPayload) {
        await this.cdlApi.post<any, AxiosResponse<any, any>, Partial<any>>(
          `/menu/v3/draft/brand/${brandId}/global-diffs/apply`,
          { ...payload },
        );
        const brandClone = cloneDeep(this.brand);
        brandClone.statuses = this.brand.statuses.map((ele) => {
          if (ele.type === BrandStatusType.APPLY_DIFF) ele.status = BrandStatusSeverity.IN_PROGRESS;
          return ele;
        });
        this.brand = brandClone;
        this.getBrandForRefresh(brandId);
      },
      async verifyBrandBarcodes(brandId: RecordEntityId): Promise<void> {
        const response = await this.cdlApi.get<Record<'warnings', BrandPublishError[]>>(
          `/menu/v3/draft/brand/${brandId}/verify-publish`,
        );

        this.brandPublishErrors = response.data?.warnings.filter((warning) => {
          return !(warning.type === 'duplicate_barcode' && !warning.barcode);
        });
      },
      async verifyV4BrandBarcodes(brandId: RecordEntityId): Promise<void> {
        const response = await this.cdlApi.get<Record<'warnings', BrandPublishError[]>>(
          `/menu/v4/brand/${brandId}/verify-publish`,
        );

        // V3's frontend filter targets a different `type` literal and is dead code for the
        // within-brand response; the server already excludes empty barcodes for V4.
        this.brandPublishErrors = response.data?.warnings ?? [];
      },
      async syncMenuWorks(
        brandId: RecordEntityId,
      ): Promise<AxiosResponse<{ process_id: string; status: BrandStatusSeverity }>> {
        return await this.cdlApi.post<{ process_id: string; status: BrandStatusSeverity }>(
          `/menu/v4/brand/${brandId}/sync-menuworks`,
        );
      },
      async getBrandMenusWithStationIds(brandId: RecordEntityId): Promise<Menu[]> {
        const filter = JSON.stringify({ station_id: { $not: { $is_null: true } } });
        const response = await this.cdlApi.get<RecordList<Menu>>(
          `/menu/v3/draft/brand/${brandId}/menus`,
          { params: { filter } },
        );
        return response.data.results;
      },
      async batchGetBrandStations(ids: string[]): Promise<StationBrand[]> {
        const { data } = await this.cdlApi.post<{ brands: StationBrand[] }>(
          '/location/brand/batch-get',
          { ids },
        );
        return data.brands;
      },
    },
    getters,
    pagination: true,
    paginationConfig: {
      ...defaultBrandNavigationPaginationConfig,
    },
  });
  return store();
}
